import InputError from '@/components/input-error';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { dashboard, logout } from '@/routes';
import { store as loginStore } from '@/routes/login';
import { request } from '@/routes/password';
import { type SharedData } from '@/types';
import { Form, Head, Link, usePage } from '@inertiajs/react';

export default function Welcome({
    canResetPassword = true,
}: {
    canResetPassword?: boolean;
}) {
    const { auth } = usePage<SharedData>().props;

    // If user is logged in, show dashboard link and logout option
    if (auth.user) {
        return (
            <>
                <Head title="Welcome" />
                <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-[#2E5984] to-[#00A5AB] p-6">
                    <Card className="w-full max-w-md">
                        <CardHeader className="text-center">
                            <CardTitle className="text-2xl">
                                Willkommen zurück, {auth.user.name}!
                            </CardTitle>
                            <CardDescription>
                                Sie sind bereits angemeldet.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-3">
                            <Link href={dashboard()}>
                                <Button className="w-full bg-[#2E5984] hover:bg-[#1e3d5c]">
                                    Zum Dashboard
                                </Button>
                            </Link>
                            <Link
                                href={logout().url}
                                method="post"
                                as="button"
                                className="w-full"
                            >
                                <Button
                                    variant="outline"
                                    className="w-full border-[#2E5984] text-[#2E5984] hover:bg-[#2E5984]/10"
                                >
                                    Abmelden
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </>
        );
    }

    return (
        <>
            <Head title="LacoHeat Planungstool">
                <link rel="preconnect" href="https://fonts.bunny.net" />
                <link
                    href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600"
                    rel="stylesheet"
                />
            </Head>
            <div className="flex min-h-screen flex-col bg-gradient-to-br from-[#2E5984] via-[#1e4a6e] to-[#00A5AB]">
                {/* Hero Section */}
                <header className="flex flex-col items-center px-6 pt-16 pb-8 text-center text-white">
                    <h1 className="mb-2 text-3xl font-semibold md:text-4xl">
                        LacoHeat Planungstool
                    </h1>
                    <p className="max-w-xl text-lg text-white/80">
                        Professionelle Planung und Verwaltung Ihrer
                        Heizungsprojekte
                    </p>
                </header>

                {/* Auth Card Section */}
                <main className="flex flex-1 items-start justify-center px-4 pb-12 md:items-center md:px-6">
                    <Card className="w-full max-w-md shadow-xl">
                        <CardHeader>
                            <CardTitle className="text-xl text-[#2E5984]">
                                Anmelden
                            </CardTitle>
                            <CardDescription>
                                Melden Sie sich mit Ihren Zugangsdaten an
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Form
                                {...loginStore.form()}
                                resetOnSuccess={['password']}
                                className="flex flex-col gap-4"
                            >
                                {({ processing, errors }) => (
                                    <>
                                        <div className="grid gap-2">
                                            <Label htmlFor="login-email">
                                                E-Mail-Adresse
                                            </Label>
                                            <Input
                                                id="login-email"
                                                type="email"
                                                name="email"
                                                required
                                                autoComplete="email"
                                                placeholder="ihre@email.de"
                                            />
                                            <InputError
                                                message={errors.email}
                                            />
                                        </div>

                                        <div className="grid gap-2">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="login-password">
                                                    Passwort
                                                </Label>
                                                {canResetPassword && (
                                                    <Link
                                                        href={request()}
                                                        className="text-sm text-[#0074AD] hover:underline"
                                                    >
                                                        Passwort vergessen?
                                                    </Link>
                                                )}
                                            </div>
                                            <Input
                                                id="login-password"
                                                type="password"
                                                name="password"
                                                required
                                                autoComplete="current-password"
                                                placeholder="Passwort"
                                            />
                                            <InputError
                                                message={errors.password}
                                            />
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <Checkbox
                                                id="remember"
                                                name="remember"
                                            />
                                            <Label
                                                htmlFor="remember"
                                                className="text-sm font-normal"
                                            >
                                                Angemeldet bleiben
                                            </Label>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="mt-2 w-full bg-[#2E5984] hover:bg-[#1e3d5c]"
                                            disabled={processing}
                                        >
                                            {processing && <Spinner />}
                                            Anmelden
                                        </Button>
                                    </>
                                )}
                            </Form>
                        </CardContent>
                    </Card>
                </main>

                {/* Footer */}
                <footer className="py-4 text-center text-sm text-white/60">
                    <p>
                        &copy; {new Date().getFullYear()} LacoHeat. Alle Rechte
                        vorbehalten.
                    </p>
                </footer>
            </div>
        </>
    );
}
